import { AuthenticationSettings } from "@account/interfaces";
import { HttpClient, HttpContext, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { IGNORE_UNAUTHORIZED } from "@bbwt/modules/data-service/http-statuses.interceptor";
import { NhsRegisterTokenStatus } from "@main/users/nhs-register-result";
import { concatMap } from "rxjs";

@Injectable()
export class LinkNhsMiproService extends BaseDataService {
    readonly url = "api/account";

    constructor(
        protected http: HttpClient,
        protected handlersFactory: HttpResponsesHandlersFactory
    ) {
        super(http, handlersFactory);
    }

    async login(request: LinkNhsMiproLoginRequest) {
        try {
            const loginResult = await this.handleRequest<AuthenticationSettings>(
                this.http.post(`${this.url}/login`, request, { context: new HttpContext().set(IGNORE_UNAUTHORIZED, true) }),
                this.handlersFactory.getDefault({ showErrorMessage: false })
            );

            return {
                invalidCredentials: loginResult.emailExistsAndPasswordNotMatch,
                loggedIn: !!loginResult.loggedUser,
                requires2FaCode: loginResult.authenticatorEnabled || loginResult.twoFactorAuthenticationFailed
            } as LinkNhsMiproLoginResponse;
        } catch (ex: unknown) {
            if (ex instanceof HttpErrorResponse && ex.status === 401) {
                return {
                    invalidCredentials: true
                } as LinkNhsMiproLoginResponse;
            }

            return { unknownError: true } as LinkNhsMiproLoginResponse;
        }
    }

    async completeLinking(request: CompleteLinkNhsMiproRequest) {
        try {
            return await this.handle(
                this.http
                    // Making this request before the one that completes the linking, is the only
                    // quick way I found to avoid a XSRF validation error (i.e., obtain a valid token
                    // for the authenticated user before posting the complete linking request)
                    .get<void>("api/user/me")
                    .pipe(
                        concatMap(() =>
                            this.http.post<CompleteLinkNhsMiproResponse>(
                                `api/end-user/self-register/${request.organizationUrl}/complete-link-nhs-mipro`,
                                request
                            )
                        )
                    )
            );
        } catch {
            return {
                success: false,
                errorMessage: "Failed to complete registration."
            } as CompleteLinkNhsMiproResponse;
        }
    }
}

export type LinkNhsMiproLoginResponse = {
    loggedIn: boolean;
    requires2FaCode: boolean;
    invalidCredentials: boolean;
    unknownError?: boolean;
};

export type LinkNhsMiproLoginRequest = {
    email: string;
    password: string;
    twoFactorCode?: string;
    browser: string;
    fingerprint: string;
    captchaResponse?: string;
};

export type CompleteLinkNhsMiproRequest = {
    nhsCode: string;
    organizationUrl: string;
    email: string;
};

export enum CompleteNhsLinkingStatus {
    Success = 0,
    NhsNonceMismatch,
    IM1RegistrationFailed
}

export type CompleteLinkNhsMiproResponse = {
    success: boolean;
    errorMessage?: string;
    status?: CompleteNhsLinkingStatus;
};
